import axios from '@/utils/axios';

/**
 * Harness 服务地址配置
 * - 'java' : 通过 Java 后端透传（走 UMI_API_SERVER，推荐）
 * - 'local': 直连本地 Flask 服务（开发调试用）
 */
const HARNESS_MODE = 'java'; // 切换此处: 'java' | 'local'
const LOCAL_FLASK_URL = 'http://localhost:8310/api/harness';

/**
 * 统一请求封装，自动处理路径拼接和 baseURL。
 */
function request(method, path, body, params) {
  if (HARNESS_MODE === 'local') {
    // 直连本地 Flask，不带 token 拦截
    const url = `${LOCAL_FLASK_URL}${path}`;
    const options = {
      method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (body) options.body = JSON.stringify(body);
    if (params) {
      const qs = new URLSearchParams(params).toString();
      return fetch(`${url}?${qs}`, options).then((r) => r.json());
    }
    return fetch(url, options).then((r) => r.json());
  }

  // 走 Java 后端透传（默认）
  const fullPath = `/actionConfig/harness${path}`;
  if (method === 'get') {
    return axios.get(fullPath, { params });
  }
  return axios.post(fullPath, { requestData: body || {} });
}

/**
 * 获取动作列表（含配置状态和视角信息）
 */
export async function getActionList() {
  return request('get', '/actions');
}

/**
 * 获取单个动作的配置预览
 */
export async function getActionConfig(actionId) {
  return request('get', `/actions/${actionId}/config`);
}

/**
 * 触发单个动作生成
 */
export async function generateSingle(actionId, model = 'qwen-plus') {
  return request('post', '/generate/single', { action_id: actionId, model });
}

/**
 * 触发批量生成
 */
export async function generateBatch(actionIds = [], model = 'qwen-plus') {
  return request('post', '/generate/batch', { action_ids: actionIds, model });
}

/**
 * 查询任务状态
 */
export async function getTaskStatus(taskId) {
  return request('get', `/tasks/${taskId}`);
}

/**
 * 取消任务
 */
export async function cancelTask(taskId) {
  return request('post', `/tasks/${taskId}/cancel`);
}

/**
 * 获取任务列表
 */
export async function listTasks() {
  return request('get', '/tasks');
}

/**
 * 获取最新生成报告
 */
export async function getReport() {
  return request('get', '/report');
}

/**
 * 获取信号参考列表
 */
export async function getSignals(category = '') {
  return request('get', '/signals', null, category ? { category } : {});
}

/**
 * 发布草稿到正式配置
 */
export async function publishDraft(actionId) {
  return request('post', `/actions/${actionId}/publish`);
}

/**
 * 保存/更新草稿配置
 */
export async function saveDraft(actionId, config) {
  return request('post', `/actions/${actionId}/draft`, { config });
}

/**
 * 获取动作视频列表
 */
export async function getActionVideos(actionId) {
  return request('get', `/videos/${actionId}`);
}

/**
 * 视频测试 — 用指定视频跑动作分析
 */
export async function testVideo(actionId, videoUrl) {
  return request('post', '/test-video', { action_id: actionId, video_url: videoUrl });
}

// ==================== 诊断记录 API ====================

/**
 * 获取所有动作的诊断摘要（Level 1）
 */
export async function getDiagnosisSummary() {
  return request('get', '/diagnosis/summary');
}

/**
 * 获取某动作的所有诊断记录列表（Level 2）
 */
export async function getDiagnosisRecords(actionId) {
  return request('get', `/diagnosis/records/${actionId}`);
}

/**
 * 获取单条诊断记录的完整详情（Level 3）
 */
export async function getDiagnosisDetail(actionId, recordId) {
  return request('get', `/diagnosis/records/${actionId}/${recordId}`);
}

/**
 * 手动触发诊断
 */
export async function runDiagnosis(actionId, actionName) {
  return request('post', '/diagnosis/run', { action_id: actionId, action_name: actionName });
}
